/**
 * MarkdownEditor - WYSIWYG Markdown editor using Vditor
 *
 * Features:
 * - Split view editing (left: source, right: preview)
 * - Customized toolbar (bold, italic, headings, lists, table, link)
 * - Controlled component interface
 * - Traditional Chinese locale
 */

import { useEffect, useRef, useState } from 'react';
import Vditor from 'vditor';
import 'vditor/dist/index.css';
import './MarkdownEditor.css';

export interface MarkdownEditorProps {
  /** Current Markdown content */
  value: string;
  /** Callback when content changes */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Editor height */
  height?: string;
}

const MarkdownEditor = ({
  value,
  onChange,
  placeholder = '請輸入商品描述...',
  height = '400px',
}: MarkdownEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const vditorRef = useRef<Vditor | undefined>(undefined);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize Vditor (async operation)
    const vditor = new Vditor(containerRef.current, {
      height,
      mode: 'sv', // Split View
      placeholder,
      lang: 'zh_TW',
      cache: { enable: false },
      value,
      toolbar: [
        'bold',
        'italic',
        'headings',
        '|',
        'list',
        'ordered-list',
        '|',
        'table',
        'link',
      ],
      input: (content) => {
        onChange(content);
      },
      after: () => {
        // Vditor is fully initialized
        setIsReady(true);
        vditorRef.current = vditor;
      },
    });

    return () => {
      if (vditorRef.current) {
        vditorRef.current.destroy();
        vditorRef.current = undefined;
      }
      setIsReady(false);
    };
  }, []); // Only initialize once

  // Update value when prop changes (e.g., when loading existing product)
  useEffect(() => {
    // Only update value after Vditor is fully initialized
    if (isReady && vditorRef.current) {
      const currentValue = vditorRef.current.getValue();
      if (value !== currentValue) {
        vditorRef.current.setValue(value);
      }
    }
  }, [value, isReady]);

  return <div ref={containerRef} className="markdown-editor" />;
};

export default MarkdownEditor;
